import { useCallback } from 'react';
import { Block, BlockActions } from '../types';
import { BlockService, BlockCalculationService } from '../services';

interface UseBlockActionsProps {
  state: {
    blocks: Record<string, Block>;
    rootBlocks: string[];
  };
  updateBlock: (id: string, updates: Partial<Block>) => void;
  addBlock: (block: Block) => void;
  removeBlock: (id: string) => void;
}

export const useBlockActions = ({ state, updateBlock, addBlock, removeBlock }: UseBlockActionsProps): BlockActions => {
  // Ajouter un bloc générique
  const addBlockAction = useCallback((parentId?: string, afterBlockId?: string) => {
    // Pour l'ajout à la fin, on ne passe pas de parentId pour les blocs racines
    const newBlock = BlockService.createBlock(undefined, 0);
    addBlock(newBlock);
    return newBlock.id;
  }, [addBlock]);

  // Ajouter une prestation — si `parentId` pointe vers un lot/section existant,
  // la nouvelle ligne est imbriquée dedans (level = parent.level + 1).
  // Sinon (parentId absent ou parent introuvable), ajout à la racine.
  const addPrestation = useCallback((parentId?: string, afterBlockId?: string) => {
    const parent = parentId ? state.blocks[parentId] : undefined;
    const targetParentId = parent ? parent.id : undefined;
    const level = parent ? parent.level + 1 : 0;
    const newBlock = BlockService.createPrestation(targetParentId, level);
    // order = après le dernier enfant du parent (ou après les racines actuelles)
    const siblingsCount = parent
      ? parent.children.length
      : state.rootBlocks.length;
    newBlock.order = siblingsCount;
    addBlock(newBlock);
    return newBlock.id;
  }, [addBlock, state.blocks, state.rootBlocks]);

  // Ajouter une section
  const addSection = useCallback((parentId?: string, afterBlockId?: string) => {
    // Pour l'ajout à la fin, on ne passe pas de parentId pour les blocs racines
    const newBlock = BlockService.createSection(undefined, 0);
    addBlock(newBlock);
    return newBlock.id;
  }, [addBlock]);

  // Mettre à jour un bloc
  const updateBlockAction = useCallback((id: string, updates: Partial<Block>) => {
    updateBlock(id, updates);
  }, [updateBlock]);

  // Supprimer un bloc
  const deleteBlock = useCallback((id: string) => {
    removeBlock(id);
  }, [removeBlock]);

  // Déplacer un bloc
  const moveBlock = useCallback((id: string, newParentId?: string, newOrder?: number) => {
    const block = state.blocks[id];
    if (!block) return;

    // Retirer de l'ancien parent
    if (block.parentId) {
      const oldParent = state.blocks[block.parentId];
      if (oldParent) {
        updateBlock(block.parentId, {
          children: oldParent.children.filter(childId => childId !== id)
        });
      }
    }

    // Ajouter au nouveau parent
    if (newParentId) {
      const newParent = state.blocks[newParentId];
      if (newParent) {
        updateBlock(newParentId, {
          children: [...newParent.children, id]
        });
      }
    }

    // Calculer le nouveau niveau
    const newLevel = newParentId ? (state.blocks[newParentId]?.level || 0) + 1 : 0;

    // Mettre à jour le bloc avec le nouveau niveau
    updateBlock(id, {
      parentId: newParentId,
      level: newLevel,
      order: newOrder || 0
    });

    // Mettre à jour récursivement les niveaux et parentId de tous les enfants
    const updateChildrenLevels = (blockId: string, parentLevel: number, newParentId: string) => {
      const currentBlock = state.blocks[blockId];
      if (!currentBlock) return;

      const newChildLevel = parentLevel + 1;
      updateBlock(blockId, { 
        level: newChildLevel,
        parentId: newParentId
      });

      // Mettre à jour les enfants récursivement
      currentBlock.children.forEach(childId => {
        updateChildrenLevels(childId, newChildLevel, blockId);
      });
    };

    // Mettre à jour les niveaux et parentId des enfants
    block.children.forEach(childId => {
      updateChildrenLevels(childId, newLevel, id);
    });
  }, [state.blocks, updateBlock]);

  // Indenter un bloc
  const indentBlock = useCallback((id: string) => {
    const block = state.blocks[id];
    if (!block || block.level >= 999) return;

    const siblings = block.parentId 
      ? BlockService.getChildren(state.blocks, block.parentId)
      : state.rootBlocks.map(blockId => state.blocks[blockId]).filter(Boolean);
    
    const currentIndex = siblings.findIndex(b => b.id === id);
    if (currentIndex <= 0) return;

    const previousSibling = siblings[currentIndex - 1];
    
    // Si le bloc précédent est une section, s'y imbriquer
    if (previousSibling && !previousSibling.content.intitule) {
      // Calculer le nouvel ordre dans la section parent
      const newOrder = previousSibling.children.length;
      moveBlock(id, previousSibling.id, newOrder);
    }
  }, [state.blocks, state.rootBlocks, moveBlock]);

  // Désindenter un bloc
  const unindentBlock = useCallback((id: string) => {
    const block = state.blocks[id];
    if (!block || block.level <= 0) return;

    const parent = BlockService.getParent(state.blocks, id);
    if (!parent) return;

    const grandParent = BlockService.getParent(state.blocks, parent.id);
    
    // Calculer le nouvel ordre dans le grand-parent ou à la racine
    let newOrder = 0;
    if (grandParent) {
      newOrder = grandParent.children.length;
    } else {
      // Si on remonte à la racine, trouver la position après le parent
      const parentIndex = state.rootBlocks.indexOf(parent.id);
      newOrder = parentIndex + 1;
    }
    
    moveBlock(id, grandParent?.id, newOrder);
  }, [state.blocks, state.rootBlocks, moveBlock]);


  // Effacer les valeurs d'un bloc
  const clearValues = useCallback((id: string) => {
    const block = state.blocks[id];
    if (block) {
      updateBlock(id, {
        content: {
          ...block.content,
          unite: '',
          quantite: '',
          prixUnitaire: '',
          prixTotal: ''
        }
      });
    }
  }, [state.blocks, updateBlock]);

  // Ajouter des valeurs par défaut à un bloc
  const addValues = useCallback((id: string) => {
    const block = state.blocks[id];
    if (block) {
      const updatedContent = {
        ...block.content,
        unite: 'unité',
        quantite: '1',
        prixUnitaire: '0'
      };
      
      // Calculer le prix total automatiquement
      const updatedBlock = { ...block, content: updatedContent };
      const calculatedTotal = BlockCalculationService.calculatePrestationTotal(updatedBlock);
      
      updateBlock(id, {
        content: {
          ...updatedContent,
          prixTotal: BlockCalculationService.formatPrice(calculatedTotal)
        }
      });
    }
  }, [state.blocks, updateBlock]);

  // Synchroniser les niveaux de tous les blocs
  const syncLevels = useCallback(() => {
    const syncedBlocks = BlockService.syncLevels(state.blocks, state.rootBlocks);
    
    // Mettre à jour tous les blocs qui ont changé de niveau
    Object.keys(syncedBlocks).forEach(blockId => {
      const originalBlock = state.blocks[blockId];
      const syncedBlock = syncedBlocks[blockId];
      
      if (originalBlock && originalBlock.level !== syncedBlock.level) {
        updateBlock(blockId, { level: syncedBlock.level });
      }
    });
  }, [state.blocks, state.rootBlocks, updateBlock]);

  return {
    addBlock: addBlockAction,
    addPrestation,
    addSection,
    updateBlock: updateBlockAction,
    deleteBlock,
    moveBlock,
    indentBlock,
    unindentBlock,
    clearValues,
    addValues,
    syncLevels
  };
};
